import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { PASSWORD_MIN_LEN, formatApiErrorDetail } from "../lib/password";
import { DISPLAY_DOMAIN } from "../lib/brand";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token. Please use the link from your email.");
      return;
    }
    if (pw1.length < PASSWORD_MIN_LEN) {
      toast.error(`Password must be at least ${PASSWORD_MIN_LEN} characters.`);
      return;
    }
    if (pw1 !== pw2) {
      toast.error("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password updated. Redirecting to sign in…");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-4 mb-8">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-16 h-16 object-contain" />
          <div className="text-center">
            <span className="text-3xl font-semibold text-platinum font-serif">EchoSense Capital Club</span>
            <span className="text-platinum/50 text-sm block">{DISPLAY_DOMAIN}</span>
          </div>
        </Link>

        <Card className="bg-white border-slate-200 shadow-xl animate-fade-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif text-slate-900">
              {done ? "Password Updated" : "Choose a New Password"}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {done
                ? "You can now sign in with your new password."
                : "Minimum 8 characters. Use a strong, unique password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="space-y-5 text-center" data-testid="reset-password-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <Link to="/login">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Go to Sign In
                  </Button>
                </Link>
              </div>
            ) : !token ? (
              <div className="text-center space-y-4">
                <p className="text-slate-600 text-sm">
                  This page requires a valid reset token. Please use the link from your email.
                </p>
                <Link to="/forgot-password">
                  <Button variant="outline" className="w-full">Request a new link</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="pw1" className="text-slate-700">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/60" />
                    <Input
                      id="pw1"
                      type={showPw ? "text" : "password"}
                      placeholder="••••••••"
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      className="bg-white border-slate-200 text-slate-900 pl-9 pr-10 placeholder:text-platinum/60"
                      minLength={8}
                      required
                      data-testid="reset-pw1-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-platinum/50 hover:text-platinum"
                    >
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pw2" className="text-slate-700">Confirm Password</Label>
                  <Input
                    id="pw2"
                    type={showPw ? "text" : "password"}
                    placeholder="••••••••"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                    minLength={8}
                    required
                    data-testid="reset-pw2-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-medium"
                  data-testid="reset-submit-button"
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
